import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import GamifiedButton from './common/GamifiedButton';
import { useLang } from '../i18n/LanguageContext';

/**
 * Démo jouable — une partie express du vrai jeu, sur la page d'accueil.
 *
 * Le visiteur vit les quatre étapes du jour J : il parie sur une équipe,
 * répond à de vraies questions du questionnaire fourni, voit le puzzle
 * s'assembler (l'illustration est le VRAI puzzle, rendue depuis les fichiers
 * d'impression), affronte la question subsidiaire, puis gratte la 12e pièce.
 * C'est la version interactive de la vidéo qu'on n'a pas.
 *
 * Garde-fous :
 *  - la question subsidiaire de la démo est VOLONTAIREMENT différente de la
 *    vraie : l'accueil ne doit pas éventer la surprise du jour J. La vraie
 *    question et sa réponse restent imprimées dans les règles ;
 *  - le résultat de la démo est tiré au sort et annoncé comme tel ;
 *  - accessibilité : tout se joue au clavier, chaque étape est annoncée en
 *    aria-live, le résultat est écrit en toutes lettres (jamais la couleur
 *    seule) et prefers-reduced-motion coupe les animations.
 */

const COLS = 3;
const ROWS = 4;

// La bulle « GIRL / BOY » de l'illustration tombe dans la case bas-centre
// d'une grille 3×4 (mesuré sur le rendu des fichiers d'impression).
const PIECE_REVELATION = 10;
const PIECES_QUESTIONS = [4, 1, 7];
const PIECES_CASCADE = [0, 2, 3, 5, 6, 8, 9, 11];

const SEUIL_REVELATION = 0.5; // proportion grattée à partir de laquelle on dévoile
const RAYON_GRATTAGE = 24;

// Seuls les éléments non textuels restent au niveau du module : le mot du
// résultat (« une FILLE » / « un GARÇON ») passe par t, donc il est ajouté
// dans le composant.
const RESULTATS = {
  girl: {
    symbole: '💗',
    image: '/photos/puzzle-fille.webp',
    fond: 'from-[#F4A6C1] to-[#E38BA9]',
  },
  boy: {
    symbole: '💙',
    image: '/photos/puzzle-garcon.webp',
    fond: 'from-[#7FB6E3] to-[#4A90C4]',
  },
};

export default function GameDemo() {
  const { t } = useLang();
  const [etape, setEtape] = useState('pari');
  const [pari, setPari] = useState(null);
  const [resultat, setResultat] = useState(() => (Math.random() < 0.5 ? 'girl' : 'boy'));
  const [qIndex, setQIndex] = useState(0);
  const [revelees, setRevelees] = useState([]);
  const [pieceGagnee, setPieceGagnee] = useState(false);
  const [estimation, setEstimation] = useState('');
  const [estimationFaite, setEstimationFaite] = useState(false);
  const [gratte, setGratte] = useState(false);
  const [aCommenceAGratter, setACommenceAGratter] = useState(false);

  const canvasRef = useRef(null);
  const zoneRef = useRef(null);
  const dessineRef = useRef(false);
  const dernierPointRef = useRef(null);
  const gratteRef = useRef(false);
  const timersRef = useRef([]);

  // t change d'identité à chaque rendu : on le lit par une ref dans le
  // grattage, pour que peindreCouche garde une identité stable. Sinon l'effet
  // du canvas se relancerait à chaque rendu et repeindrait la couche
  // argentée — le grattage en cours serait effacé.
  const tRef = useRef(t);
  tRef.current = t;

  // Ces textes vivaient au niveau du module ; ils descendent ici car ils
  // passent par t (un hook).
  // Questions réelles, reprises telles quelles du questionnaire fourni dans le jeu.
  const QUESTIONS_DEMO = [
    { numero: 5, texte: t('demo.question.5') },
    { numero: 7, texte: t('demo.question.7') },
    { numero: 11, texte: t('demo.question.11') },
  ];

  const ETAPES_LIBELLES = {
    pari: t('demo.etape.pari'),
    questions: t('demo.etape.questions'),
    cascade: t('demo.etape.cascade'),
    subsidiaire: t('demo.etape.subsidiaire'),
    grattage: t('demo.etape.grattage'),
    final: t('demo.etape.final'),
  };

  const r = {
    ...RESULTATS[resultat],
    mot: resultat === 'girl' ? t('demo.resultat.fille') : t('demo.resultat.garcon'),
  };

  // Les deux illustrations se préchargent dès l'affichage : au moment où les
  // pièces se révèlent, l'image doit déjà être là.
  useEffect(() => {
    Object.values(RESULTATS).forEach((res) => {
      const img = new Image();
      img.src = res.image;
    });
  }, []);

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const programmer = (fn, delai) => {
    timersRef.current.push(setTimeout(fn, delai));
  };

  /* ---------------------------------------------------------- étapes */

  const parier = (equipe) => {
    setPari(equipe);
    setEtape('questions');
  };

  const repondre = () => {
    if (pieceGagnee) return;
    setPieceGagnee(true);
    setRevelees((prev) => [...prev, PIECES_QUESTIONS[qIndex]]);
    programmer(() => {
      setPieceGagnee(false);
      if (qIndex + 1 < QUESTIONS_DEMO.length) {
        setQIndex(qIndex + 1);
      } else {
        setEtape('cascade');
      }
    }, 1100);
  };

  // Les huit pièces restantes tombent en cascade, puis place à la subsidiaire.
  useEffect(() => {
    if (etape !== 'cascade') return;
    PIECES_CASCADE.forEach((piece, i) => {
      programmer(() => setRevelees((prev) => (prev.includes(piece) ? prev : [...prev, piece])), 160 * i);
    });
    programmer(() => setEtape('subsidiaire'), 160 * PIECES_CASCADE.length + 900);
  }, [etape]); // eslint-disable-line react-hooks/exhaustive-deps

  const validerEstimation = (event) => {
    event.preventDefault();
    if (estimation.trim() !== '') setEstimationFaite(true);
  };

  /* ---------------------------------------------------- pièce à gratter */

  const peindreCouche = useCallback(() => {
    const canvas = canvasRef.current;
    const zone = zoneRef.current;
    if (!canvas || !zone) return;

    const { width, height } = zone.getBoundingClientRect();
    if (!width || !height) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, width, height);

    // Aspect « encre argentée » de la vraie pièce à gratter.
    const degrade = ctx.createLinearGradient(0, 0, width, height);
    degrade.addColorStop(0, '#C9CDCB');
    degrade.addColorStop(0.35, '#EDEFEE');
    degrade.addColorStop(0.5, '#B9BFBC');
    degrade.addColorStop(0.75, '#E4E7E5');
    degrade.addColorStop(1, '#A9B0AD');
    ctx.fillStyle = degrade;
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    for (let i = 0; i < Math.round((width * height) / 900); i += 1) {
      ctx.fillRect(Math.random() * width, Math.random() * height, 1, 1);
    }

    ctx.fillStyle = 'rgba(51,64,59,0.55)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '700 14px system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.fillText(tRef.current('demo.grattage.grattez'), width / 2, height / 2 - 9);
    ctx.font = '500 11px system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.fillText(tRef.current('demo.grattage.doigtOuSouris'), width / 2, height / 2 + 12);

    ctx.globalCompositeOperation = 'destination-out';
  }, []);

  const reveler = useCallback(() => {
    if (gratteRef.current) return;
    gratteRef.current = true;
    setGratte(true);
  }, []);

  // On efface réellement la couche en plus du fondu : si la transition CSS ne
  // tourne pas (onglet en arrière-plan, animations coupées), rien ne doit
  // rester par-dessus la révélation.
  useEffect(() => {
    if (!gratte) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // La pièce rejoint le puzzle, puis le verdict tombe.
    programmer(() => {
      setRevelees((prev) => (prev.includes(PIECE_REVELATION) ? prev : [...prev, PIECE_REVELATION]));
      setEtape('final');
    }, 1400);
  }, [gratte]); // eslint-disable-line react-hooks/exhaustive-deps

  const mesurerProgression = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.width) return;
    const ctx = canvas.getContext('2d');
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let vides = 0;
    let total = 0;
    for (let i = 3; i < data.length; i += 4 * 32) {
      if (data[i] === 0) vides += 1;
      total += 1;
    }
    if (total && vides / total > SEUIL_REVELATION) reveler();
  }, [reveler]);

  const positionDans = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const source = event.touches?.[0] ?? event;
    return { x: source.clientX - rect.left, y: source.clientY - rect.top };
  };

  const gratter = useCallback((point) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const precedent = dernierPointRef.current;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = RAYON_GRATTAGE * 2;
    ctx.beginPath();
    if (precedent) {
      ctx.moveTo(precedent.x, precedent.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }
    ctx.arc(point.x, point.y, RAYON_GRATTAGE, 0, Math.PI * 2);
    ctx.fill();
    dernierPointRef.current = point;
  }, []);

  useEffect(() => {
    if (etape !== 'grattage') return undefined;
    peindreCouche();

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const demarrer = (event) => {
      if (gratteRef.current) return;
      event.preventDefault();
      dessineRef.current = true;
      dernierPointRef.current = null;
      setACommenceAGratter(true);
      gratter(positionDans(event));
    };
    const deplacer = (event) => {
      if (!dessineRef.current || gratteRef.current) return;
      event.preventDefault();
      gratter(positionDans(event));
    };
    const arreter = () => {
      if (!dessineRef.current) return;
      dessineRef.current = false;
      dernierPointRef.current = null;
      mesurerProgression();
    };

    canvas.addEventListener('mousedown', demarrer);
    canvas.addEventListener('mousemove', deplacer);
    canvas.addEventListener('touchstart', demarrer, { passive: false });
    canvas.addEventListener('touchmove', deplacer, { passive: false });
    window.addEventListener('mouseup', arreter);
    window.addEventListener('touchend', arreter);

    const surRedimensionnement = () => {
      if (!gratteRef.current) peindreCouche();
    };
    window.addEventListener('resize', surRedimensionnement);

    return () => {
      canvas.removeEventListener('mousedown', demarrer);
      canvas.removeEventListener('mousemove', deplacer);
      canvas.removeEventListener('touchstart', demarrer);
      canvas.removeEventListener('touchmove', deplacer);
      window.removeEventListener('mouseup', arreter);
      window.removeEventListener('touchend', arreter);
      window.removeEventListener('resize', surRedimensionnement);
    };
  }, [etape, gratter, mesurerProgression, peindreCouche]);

  const rejouer = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    gratteRef.current = false;
    dernierPointRef.current = null;
    setGratte(false);
    setACommenceAGratter(false);
    setEstimation('');
    setEstimationFaite(false);
    setQIndex(0);
    setRevelees([]);
    setPieceGagnee(false);
    setPari(null);
    // On tire l'autre résultat : rejouer doit apprendre quelque chose.
    setResultat((prev) => (prev === 'girl' ? 'boy' : 'girl'));
    setEtape('pari');
  };

  /* ------------------------------------------------------------ rendu */

  const question = QUESTIONS_DEMO[qIndex];

  return (
    <section
      id="demo-jeu"
      className="demo-jeu relative overflow-hidden bg-gradient-to-br from-[#17453E] via-[#22403A] to-[#33403B] px-5 py-16 md:py-20"
    >
      <div className="mx-auto max-w-[1000px] text-center">
        <p className="m-0 mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#EDB82F]">
          {t('demo.badge')}
        </p>
        <h2 className="m-0 mb-4 text-3xl font-black uppercase tracking-tight text-white md:text-4xl">
          {t('demo.titre')}
        </h2>
        <p className="mx-auto m-0 mb-9 max-w-[600px] text-[15px] leading-relaxed text-white/75">
          {t('demo.intro')}
        </p>

        {/* Annonce d'étape pour les lecteurs d'écran */}
        <p aria-live="polite" className="sr-only">
          {ETAPES_LIBELLES[etape]}
          {etape === 'final' ? ` ${t('demo.aria.resultat', { mot: r.mot })}` : ''}
        </p>

        <div className="flex flex-col items-center justify-center gap-8 lg:flex-row lg:items-start lg:gap-12">
          {/* ------------------------------------------------ le puzzle */}
          <div className="shrink-0">
            <div
              className="relative grid w-[min(72vw,300px)] grid-cols-3 gap-[3px] overflow-hidden rounded-2xl border-4 border-[#EDB82F]/80 bg-[#101E1B] p-[3px] shadow-[0_24px_60px_rgba(0,0,0,0.35)]"
              style={{ aspectRatio: '938 / 1406' }}
              role="img"
              aria-label={t('demo.puzzle.aria', { n: revelees.length })}
            >
              {Array.from({ length: COLS * ROWS }, (_, i) => {
                const col = i % COLS;
                const row = Math.floor(i / COLS);
                const visible = revelees.includes(i);
                const estPieceFinale = i === PIECE_REVELATION;
                return (
                  <div key={i} className="relative overflow-hidden rounded-[4px]">
                    {/* La tranche de l'illustration réelle du puzzle */}
                    <div
                      className="absolute inset-0"
                      style={{
                        backgroundImage: `url(${r.image})`,
                        backgroundSize: `${COLS * 100}% ${ROWS * 100}%`,
                        backgroundPosition: `${(col * 100) / (COLS - 1)}% ${(row * 100) / (ROWS - 1)}%`,
                      }}
                    />
                    {/* Le couvercle « carton » qui saute quand la pièce est gagnée */}
                    <div
                      className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br transition-all duration-500 ${
                        estPieceFinale && !visible
                          ? 'from-[#2C4A43] to-[#1C332E]'
                          : 'from-[#8A7355] to-[#6E5A41]'
                      } ${visible ? 'pointer-events-none rotate-6 scale-75 opacity-0' : 'opacity-100'}`}
                      style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' }}
                      aria-hidden="true"
                    >
                      {estPieceFinale ? (
                        <span className="text-lg" title={t('demo.puzzle.pieceAGratter')}>
                          🪙
                        </span>
                      ) : (
                        <span className="text-sm font-black text-white/30">?</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="m-0 mt-3 text-[13px] font-bold text-white/60">
              {t('demo.puzzle.compteur', { n: revelees.length })}
            </p>
          </div>

          {/* ------------------------------------------------ la partie */}
          <div className="w-full max-w-[440px]">
            {etape === 'pari' && (
              <div className="demo-carte rounded-2xl bg-white/95 p-6 text-left shadow-xl">
                <p className="m-0 mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#17453E]/60">
                  {t('demo.pari.etiquette')}
                </p>
                <h3 className="m-0 mb-4 text-xl font-black text-[#17453E]">
                  {t('demo.pari.titre')}
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => parier('girl')}
                    data-cta="home:demo-pari-fille"
                    className="rounded-xl border-2 border-[#F4A6C1] bg-[#FDF2F6] px-4 py-4 text-sm font-black text-[#B04A6E] transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F4A6C1]"
                  >
                    {t('demo.pari.fille')}
                  </button>
                  <button
                    type="button"
                    onClick={() => parier('boy')}
                    data-cta="home:demo-pari-garcon"
                    className="rounded-xl border-2 border-[#7FB6E3] bg-[#F0F7FD] px-4 py-4 text-sm font-black text-[#2F6A9E] transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-[#7FB6E3]"
                  >
                    {t('demo.pari.garcon')}
                  </button>
                </div>
                <p className="m-0 mt-3 text-xs leading-relaxed text-[#17453E]/60">
                  {t('demo.pari.note')}
                </p>
              </div>
            )}

            {etape === 'questions' && (
              <div key={qIndex} className="demo-carte rounded-2xl bg-white/95 p-6 text-left shadow-xl">
                <p className="m-0 mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#17453E]/60">
                  {t('demo.questions.etiquette', { numero: question.numero })}
                </p>
                <h3 className="m-0 mb-4 text-lg font-black leading-snug text-[#17453E]">
                  {question.texte}
                </h3>
                {pieceGagnee ? (
                  <p
                    className="demo-gagne m-0 rounded-xl bg-[#EDB82F]/15 px-4 py-4 text-center text-sm font-black text-[#8A6A0F]"
                    role="status"
                  >
                    {t('demo.questions.gagne')}
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={repondre}
                      data-cta="home:demo-reponse"
                      className="rounded-xl border-2 border-[#17453E]/20 bg-white px-4 py-4 text-sm font-black text-[#17453E] transition-all hover:-translate-y-0.5 hover:border-[#EDB82F] hover:shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-[#EDB82F]"
                    >
                      {t('demo.questions.maman')}
                    </button>
                    <button
                      type="button"
                      onClick={repondre}
                      data-cta="home:demo-reponse"
                      className="rounded-xl border-2 border-[#17453E]/20 bg-white px-4 py-4 text-sm font-black text-[#17453E] transition-all hover:-translate-y-0.5 hover:border-[#EDB82F] hover:shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-[#EDB82F]"
                    >
                      {t('demo.questions.papa')}
                    </button>
                  </div>
                )}
                <p className="m-0 mt-3 text-xs leading-relaxed text-[#17453E]/60">
                  {t('demo.questions.note')}
                </p>
              </div>
            )}

            {etape === 'cascade' && (
              <div className="demo-carte rounded-2xl bg-white/95 p-6 text-left shadow-xl">
                <h3 className="m-0 mb-2 text-lg font-black text-[#17453E]">
                  {t('demo.cascade.titre')}
                </h3>
                <p className="m-0 text-sm leading-relaxed text-[#17453E]/70">
                  {t('demo.cascade.texte')}
                </p>
              </div>
            )}

            {etape === 'subsidiaire' && (
              <div className="demo-carte rounded-2xl bg-white/95 p-6 text-left shadow-xl">
                <p className="m-0 mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#17453E]/60">
                  {t('demo.subsidiaire.etiquette')}
                </p>
                <h3 className="m-0 mb-4 text-lg font-black leading-snug text-[#17453E]">
                  {t('demo.subsidiaire.question')}
                </h3>
                {estimationFaite ? (
                  <div>
                    <p className="m-0 mb-4 text-sm leading-relaxed text-[#17453E]/80">
                      {t('demo.subsidiaire.retourAvant')} <strong>{estimation}</strong>
                      {t('demo.subsidiaire.retourApres')}{' '}
                      {t('demo.subsidiaire.retourPiece')}
                      <sup>{t('demo.subsidiaire.retourExposant')}</sup>{' '}
                      {t('demo.subsidiaire.retourFin')}
                    </p>
                    <GamifiedButton
                      variant="primary"
                      size="lg"
                      onClick={() => setEtape('grattage')}
                      data-cta="home:demo-vers-grattage"
                    >
                      {t('demo.subsidiaire.cta')}
                    </GamifiedButton>
                  </div>
                ) : (
                  <form onSubmit={validerEstimation} className="flex flex-wrap items-center gap-3">
                    <label htmlFor="demo-estimation" className="sr-only">
                      {t('demo.subsidiaire.labelEstimation')}
                    </label>
                    <input
                      id="demo-estimation"
                      type="number"
                      min="0"
                      inputMode="numeric"
                      value={estimation}
                      onChange={(e) => setEstimation(e.target.value)}
                      placeholder={t('demo.subsidiaire.placeholder')}
                      className="w-40 rounded-xl border-2 border-[#17453E]/20 px-4 py-3 text-sm font-bold text-[#17453E] focus:border-[#EDB82F] focus:outline-none"
                    />
                    <button
                      type="submit"
                      data-cta="home:demo-estimation"
                      className="rounded-xl bg-[#17453E] px-5 py-3 text-sm font-black text-white transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-[#EDB82F]"
                    >
                      {t('demo.subsidiaire.valider')}
                    </button>
                  </form>
                )}
              </div>
            )}

            {etape === 'grattage' && (
              <div className="demo-carte rounded-2xl bg-white/95 p-6 text-center shadow-xl">
                <p className="m-0 mb-3 text-[11px] font-bold uppercase tracking-[0.15em] text-[#17453E]/60">
                  {t('demo.grattage.etiquette')}
                </p>
                <div
                  ref={zoneRef}
                  className="relative mx-auto w-[min(70vw,240px)] select-none overflow-hidden rounded-xl border-4 border-[#EDB82F] shadow-lg"
                  style={{ aspectRatio: '312 / 351' }}
                >
                  {/* Sous la couche : la vraie tranche de l'illustration, celle de la bulle */}
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `url(${r.image})`,
                      backgroundSize: `${COLS * 100}% ${ROWS * 100}%`,
                      backgroundPosition: `${((PIECE_REVELATION % COLS) * 100) / (COLS - 1)}% ${(Math.floor(PIECE_REVELATION / COLS) * 100) / (ROWS - 1)}%`,
                    }}
                  />
                  <canvas
                    ref={canvasRef}
                    aria-hidden="true"
                    className={`absolute inset-0 h-full w-full touch-none transition-opacity duration-700 ${
                      gratte ? 'pointer-events-none opacity-0' : 'cursor-crosshair opacity-100'
                    }`}
                  />
                </div>
                <p aria-live="polite" className="sr-only">
                  {gratte ? t('demo.grattage.aria', { mot: r.mot }) : ''}
                </p>
                {gratte ? (
                  <p className="m-0 mt-4 text-sm font-black text-[#17453E]" role="status">
                    {r.symbole} {t('demo.grattage.revele', { mot: r.mot })}
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={reveler}
                    data-cta="home:demo-reveler"
                    className="mt-4 rounded-xl border-2 border-[#17453E]/20 px-5 py-3 text-sm font-bold text-[#17453E] transition-all hover:border-[#EDB82F] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#EDB82F]"
                  >
                    {aCommenceAGratter
                      ? t('demo.grattage.revelerMaintenant')
                      : t('demo.grattage.revelerSansGratter')}
                  </button>
                )}
              </div>
            )}

            {etape === 'final' && (
              <div
                className={`demo-carte rounded-2xl bg-gradient-to-br p-6 text-center shadow-xl ${r.fond}`}
              >
                <span className="text-4xl" aria-hidden="true">
                  {r.symbole}
                </span>
                <h3 className="m-0 mb-1 mt-2 text-2xl font-black uppercase tracking-tight text-white">
                  {t('demo.final.titre', { mot: r.mot })}
                </h3>
                <p className="m-0 mb-4 text-sm font-bold text-white/90">
                  {pari === resultat ? t('demo.final.pariGagne') : t('demo.final.pariPerdu')}
                </p>
                <p className="m-0 mb-5 text-[13px] leading-relaxed text-white/85">
                  {t('demo.final.texteAvant')} <strong>{t('demo.final.texteFort')}</strong>{' '}
                  {t('demo.final.texteApres')}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Link to="/shop" className="no-underline">
                    <GamifiedButton variant="primary" size="lg" data-cta="home:demo-vers-boutique">
                      {t('demo.final.ctaBoutique')}
                    </GamifiedButton>
                  </Link>
                  <button
                    type="button"
                    onClick={rejouer}
                    data-cta="home:demo-rejouer"
                    className="rounded-xl border-2 border-white/50 px-5 py-3 text-sm font-bold text-white transition-all hover:border-white focus:outline-none focus-visible:ring-4 focus-visible:ring-white"
                  >
                    {t('demo.final.rejouer')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .demo-jeu .demo-carte { animation: demo-apparait 0.35s ease-out; }
        .demo-jeu .demo-gagne { animation: demo-pop 0.45s ease-out; }
        @keyframes demo-apparait {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes demo-pop {
          0% { transform: scale(0.85); }
          60% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .demo-jeu *, .demo-jeu canvas { animation: none !important; transition: none !important; }
        }
      `}</style>
    </section>
  );
}
